import { Fragment } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import MuiDataGrid from 'components/MuiDataGrid';
import { IUpedateNursingRecord } from 'apis/survey/type';
import useI18n from 'hooks/useI18n';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';

interface Props {
  list: IUpedateNursingRecord[];
  isLoading: boolean;
  totalCount: number;
  page: number;
  onPageChange: (page: number) => void;
}

// Record type configuration
const RECORD_TYPES = {
  NANDA: 0,
  SOAPIE: 1,
  FOCUS_DAR: 2,
  NARRATIVE: 3,
  NURSING_PROCESS: 4,
  OMAHA: 6,
} as const;

const LABELS = {
  [RECORD_TYPES.NANDA]: {
    title: 'Nanda',
    fields: [
      'Domain',
      'Class',
      'Diagnosis',
      'Data (Subjective / Objective)',
      'Goal (Short-term / Long-term)',
      'Plan',
      'Interventions / Theoretical Basis',
      'Evaluation',
    ],
  },
  [RECORD_TYPES.SOAPIE]: {
    title: 'SOAPIE',
    fields: [
      'Subjective Data',
      'Objective Data',
      'Assessment',
      'Planning',
      'Interventions',
      'Evaluation',
    ],
  },
  [RECORD_TYPES.FOCUS_DAR]: {
    title: 'Focus DAR',
    fields: ['Focus', 'Data', 'Action', 'Response'],
  },
  [RECORD_TYPES.NARRATIVE]: {
    title: 'Narrative Notes',
    fields: ['Narrative Notes'],
  },
  [RECORD_TYPES.NURSING_PROCESS]: {
    title: 'Nursing Process',
    fields: [
      'Nursing Assessment',
      'Nursing Diagnosis',
      '',
      'Nursing Goal',
      'Nursing Plan',
      'Nursing Interventions / Theoretical Basis',
      'Nursing Evaluation',
    ],
  },
  [RECORD_TYPES.OMAHA]: {
    title: 'OMAHA',
    fields: [
      'Domain',
      'Problem',
      'Modifier 1',
      'Modifier 2',
      'Priority',
      'Sign / Symptom',
      '',
      'Intervention Scheme',
      'Problem Rating Scale for Outcomes',
    ],
  },
};

function ContentsBody(props: Props) {
  const i18n = useI18n();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const pageSize = params.get('size');
  const { palette } = useTheme();
  const { list, isLoading, totalCount, page, onPageChange } = props;

  const renderStudentHeader = (studentName: string, studentNo: string) => (
    <Stack direction="row" mt={1}>
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 'bold',
          color: palette.primary.main,
        }}
      >
        {studentName}
      </Typography>
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#00000052',
          margin: '0 5px 0 5px',
        }}
      >
        |
      </Typography>
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 'bold',
          color: palette.primary.main,
        }}
      >
        {studentNo}
      </Typography>
    </Stack>
  );

  const renderRecordHeader = (title: string, timestamp: string) => (
    <Stack direction="row" mt={1} mb={1}>
      <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 500,
          color: '#00000052',
          marginLeft: '10px',
        }}
      >
        {timestamp}
      </Typography>
    </Stack>
  );

  const renderFieldLabel = (label: string) => (
    <Typography
      component="li"
      variant="caption"
      fontWeight="bold"
      sx={{ margin: '0 10px 0 10px' }}
    >
      {label}
    </Typography>
  );

  const renderFieldValue = (value: string, marginLeft = '20px') => (
    <Typography
      variant="caption"
      sx={{
        pl: 1,
        whiteSpace: 'pre-wrap',
        marginLeft,
      }}
    >
      {value}
    </Typography>
  );

  const renderBasicRecord = (
    type: number,
    categoryKeys: string[],
    jsonCategory: any,
    timestamp: string,
    hasTopBorder = false
  ) => {
    const config = LABELS[type as keyof typeof LABELS];

    return (
      <Box
        sx={{
          margin: '0 0 5px 20px',
          ...(hasTopBorder && {
            borderTop: '1px solid lightgray',
            width: '80vw',
          }),
        }}
      >
        {renderRecordHeader(config.title, timestamp)}
        {categoryKeys.map((title: string, i: number) => (
          <Fragment key={title}>
            {renderFieldLabel(config.fields[i])}
            {renderFieldValue(jsonCategory[title])}
          </Fragment>
        ))}
      </Box>
    );
  };

  const renderNarrativeRecord = (
    categoryKeys: string[],
    jsonCategory: any,
    timestamp: string
  ) => (
    <Box
      sx={{
        margin: '0 0 5px 20px',
        borderTop: '1px solid lightgray',
        width: '80vw',
      }}
    >
      {renderRecordHeader('Narrative Notes', timestamp)}
      {categoryKeys.map((title: string) => (
        <Fragment key={title}>
          {renderFieldLabel('Narrative Notes')}
          <Box sx={{ marginLeft: '30px' }}>
            <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
              {jsonCategory[title]}
            </Typography>
          </Box>
        </Fragment>
      ))}
    </Box>
  );

  const renderNursingProcessRecord = (
    categoryKeys: string[],
    jsonCategory: any,
    timestamp: string
  ) => (
    <Box
      sx={{
        margin: '0 0 5px 20px',
        borderTop: '1px solid lightgray',
        width: '80vw',
      }}
    >
      {renderRecordHeader('Nursing Process', timestamp)}
      {categoryKeys.map((title: string, i: number) => {
        if (title === 'diagnosisRelate') {
          return (
            <Fragment key={title}>
              {renderFieldLabel('Nursing Diagnosis Diagnosis')}
              {renderFieldValue(jsonCategory[title])}
              <br />
              {renderFieldValue('Related to')}
              <br />
            </Fragment>
          );
        }
        if (title === 'diagnosis') {
          return (
            <Fragment key={title}>
              {renderFieldValue(jsonCategory[title])}
            </Fragment>
          );
        }
        return (
          <Fragment key={title}>
            {renderFieldLabel(LABELS[RECORD_TYPES.NURSING_PROCESS].fields[i])}
            {renderFieldValue(jsonCategory[title])}
          </Fragment>
        );
      })}
    </Box>
  );

  const renderOmahaSpecialField = (
    title: string,
    jsonCategory: any
  ): JSX.Element | null => {
    const fieldConfigs: Record<string, { label: string; marginLeft: string }> =
      {
        sign_sx: {
          label: 'Sign, Symptom',
          marginLeft: '12px',
        },
        sign_sx_etc: {
          label: '',
          marginLeft: '40px',
        },
        omaha_target1: {
          label:
            'Teaching, Guidance, and Counseling - Targets and Client-Specific Information',
          marginLeft: '30px',
        },
        omaha_target2: {
          label:
            'Treatments and Procedures - Targets and Client-Specific Information',
          marginLeft: '30px',
        },
        omaha_target3: {
          label: 'Case Management - Targets and Client-Specific Information',
          marginLeft: '30px',
        },
        omaha_target4: {
          label: 'Surveillance - Targets and Client-Specific Information',
          marginLeft: '30px',
        },
        omaha_knowledge_selct: {
          label: 'Knowledge',
          marginLeft: '30px',
        },
        omaha_knowledge: {
          label: '',
          marginLeft: '50px',
        },
        omaha_behavior_selct: {
          label: 'Behavior',
          marginLeft: '30px',
        },
        omaha_behavior: {
          label: '',
          marginLeft: '50px',
        },
        omaha_status_selct: {
          label: 'Status',
          marginLeft: '30px',
        },
        omaha_status: {
          label: '',
          marginLeft: '50px',
        },
      };

    const config = fieldConfigs[title];
    if (!config) return null;

    const isMainCategory = [
      'sign_sx',
      'omaha_target1',
      'omaha_knowledge_selct',
      'omaha_behavior_selct',
      'omaha_status_selct',
    ].includes(title);

    const isSubcategory =
      title.startsWith('omaha_target') && title !== 'omaha_target1';

    let displayValue = jsonCategory[title];
    if (title === 'sign_sx' && Array.isArray(displayValue)) {
      displayValue = displayValue.join(', ');
    }

    if (title === 'sign_sx') {
      return (
        <Fragment key={title}>
          <Typography
            component="li"
            variant="caption"
            fontWeight="bold"
            sx={{ marginLeft: '10px' }}
          >
            {config.label}
          </Typography>
          <Typography
            component="li"
            variant="caption"
            sx={{
              pl: 1,
              whiteSpace: 'pre-wrap',
              marginLeft: config.marginLeft,
              marginBottom: '-20px',
            }}
          >
            {displayValue}
          </Typography>
          <br />
        </Fragment>
      );
    }

    if (title === 'omaha_target1') {
      return (
        <Fragment key={title}>
          <Typography
            component="li"
            variant="caption"
            fontWeight="bold"
            sx={{ marginLeft: '10px' }}
          >
            Intervention Scheme
          </Typography>
          <Typography
            component="li"
            variant="caption"
            marginLeft="20px"
            sx={{ fontSize: '12px' }}
          >
            {config.label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              pl: 1,
              whiteSpace: 'pre-wrap',
              color: '#0000008e',
              marginLeft: config.marginLeft,
            }}
          >
            - {displayValue}
          </Typography>
        </Fragment>
      );
    }

    if (isSubcategory) {
      return (
        <Fragment key={title}>
          <Typography
            component="li"
            variant="caption"
            marginLeft="20px"
            sx={{ fontSize: '12px' }}
          >
            {config.label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              pl: 1,
              whiteSpace: 'pre-wrap',
              color: '#0000008e',
              marginLeft: config.marginLeft,
            }}
          >
            - {displayValue}
          </Typography>
        </Fragment>
      );
    }

    if (title === 'omaha_knowledge_selct') {
      return (
        <Fragment key={title}>
          <Typography component="li" variant="caption" fontWeight="bold">
            Problem Rating Scale for Outcomes
          </Typography>
          <Typography
            component="li"
            variant="caption"
            marginLeft="20px"
            sx={{ fontSize: '12px' }}
          >
            {config.label}
          </Typography>
          <Typography
            component="li"
            variant="caption"
            sx={{
              pl: 1,
              whiteSpace: 'pre-wrap',
              color: '#0000008e',
              marginLeft: config.marginLeft,
            }}
          >
            {displayValue}
          </Typography>
        </Fragment>
      );
    }

    if (isMainCategory) {
      return (
        <Fragment key={title}>
          <Typography
            component="li"
            variant="caption"
            marginLeft="20px"
            sx={{ fontSize: '12px' }}
          >
            {config.label}
          </Typography>
          <Typography
            component="li"
            variant="caption"
            sx={{
              pl: 1,
              whiteSpace: 'pre-wrap',
              color: '#0000008e',
              marginLeft: config.marginLeft,
            }}
          >
            {displayValue}
          </Typography>
        </Fragment>
      );
    }

    return (
      <Fragment key={title}>
        <Typography
          variant="caption"
          sx={{
            pl: 1,
            whiteSpace: 'pre-wrap',
            color: '#0000008e',
            marginLeft: config.marginLeft,
          }}
        >
          - {displayValue}
        </Typography>
      </Fragment>
    );
  };

  const renderOmahaRecord = (
    categoryKeys: string[],
    jsonCategory: any,
    timestamp: string
  ) => (
    <Box sx={{ margin: '0 0 5px 20px' }}>
      {renderRecordHeader('OMAHA', timestamp)}
      {categoryKeys.map((title: string, i: number) => {
        const specialField = renderOmahaSpecialField(title, jsonCategory);
        if (specialField) return specialField;

        return (
          <Fragment key={title}>
            {renderFieldLabel(LABELS[RECORD_TYPES.OMAHA].fields[i])}
            {renderFieldValue(jsonCategory[title])}
          </Fragment>
        );
      })}
    </Box>
  );

  const renderRecord = (type: number, record: any) => {
    const content = JSON.parse(record.content);
    const categoryKeys = Object.keys(content);
    const timestamp = record.record_time;

    switch (type) {
      case RECORD_TYPES.NANDA:
        return renderBasicRecord(type, categoryKeys, content, timestamp, false);

      case RECORD_TYPES.SOAPIE:
      case RECORD_TYPES.FOCUS_DAR:
        return renderBasicRecord(type, categoryKeys, content, timestamp, true);

      case RECORD_TYPES.NARRATIVE:
        return renderNarrativeRecord(categoryKeys, content, timestamp);

      case RECORD_TYPES.NURSING_PROCESS:
        return renderNursingProcessRecord(categoryKeys, content, timestamp);

      case RECORD_TYPES.OMAHA:
        return renderOmahaRecord(categoryKeys, content, timestamp);

      default:
        return null;
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'student_name',
      headerName: '',
      renderCell: params => {
        if (!params.row.student_nursing_record) {
          return <>No Record</>;
        }

        return (
          <Box sx={{ borderBottom: '3px solid black', width: '100%' }}>
            {renderStudentHeader(
              params.row.student_name,
              params.row.student_no
            )}
            {params.row.student_nursing_record.map((record: any, i: number) => (
              <Fragment key={i}>
                {renderRecord(record.record_type, record)}
              </Fragment>
            ))}
          </Box>
        );
      },
    },
  ];

  return (
    <MuiDataGrid
      headerHeight={0}
      columns={columns}
      loading={isLoading}
      onPageChange={onPageChange}
      page={page}
      pageSize={Number(pageSize) || 20}
      rows={list
        .filter(item => item.student_nursing_record?.length > 0)
        .map(item => ({ ...item, id: item.student_uuid }))}
      rowCount={totalCount}
      getRowHeight={() => 'auto'}
    />
  );
}

export default ContentsBody;
